import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Navigation,
  Activity,
  Clock,
  Plus,
  BarChart,
  Star,
  IndianRupee,
  Users,
  MapPin,
  TrendingUp
} from 'lucide-react';
import { OverviewPageLayout, KPICard, QuickActionCard } from '../../components/ui/OverviewPage';
import { Button } from '../../components/ui/Button/Button';
import { TRIP_STATUS } from '../../store/tripSlice';

export function TripOverviewPage() {
  const trips = useSelector(state => state.trips.trips);
  
  // Calculate KPI metrics
  const totalTrips = trips.length;
  const activeTrips = trips.filter(trip => trip.status === TRIP_STATUS.ONGOING).length;
  const completedTrips = trips.filter(trip => trip.status === TRIP_STATUS.COMPLETED).length;
  const cancelledTrips = trips.filter(trip => trip.status === TRIP_STATUS.CANCELLED).length;
  
  const totalRevenue = trips
    .filter(trip => trip.status === TRIP_STATUS.COMPLETED)
    .reduce((sum, trip) => sum + trip.fare.total, 0);
  
  const averageRating = trips
    .filter(trip => trip.rating?.passenger)
    .reduce((sum, trip, _, arr) => sum + trip.rating.passenger / arr.length, 0);
  
  const completionRate = totalTrips > 0 ? (completedTrips / totalTrips) * 100 : 0;
  
  // Recent activity - last 7 days
  const lastWeek = new Date();
  lastWeek.setDate(lastWeek.getDate() - 7);
  const recentTrips = trips.filter(trip => new Date(trip.requestedAt) >= lastWeek);
  const weeklyGrowth = recentTrips.length > 0 ? ((recentTrips.length / totalTrips) * 100) : 0;

  const kpiCards = [
    {
      title: 'Total Trips',
      value: totalTrips,
      change: weeklyGrowth,
      icon: Navigation,
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      isCount: true
    },
    {
      title: 'Active Trips',
      value: activeTrips,
      change: 0, // Real-time data would show change
      icon: Activity,
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      isCount: true
    },
    {
      title: 'Total Revenue',
      value: totalRevenue,
      change: 12.5, // Mock growth percentage
      icon: IndianRupee,
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Completion Rate',
      value: completionRate,
      change: 2.1,
      icon: TrendingUp,
      color: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600',
      formatValue: (value) => `${value.toFixed(1)}%`
    }
  ];

  const quickActions = [
    {
      title: 'Create New Trip',
      description: 'Manually create and assign a new trip',
      icon: Plus,
      link: '/trips/create',
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      title: 'Monitor Active Trips',
      description: 'View and manage ongoing trips in real-time',
      icon: Activity,
      link: '/trips/active',
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      title: 'Trip Analytics',
      description: 'View detailed trip performance and insights',
      icon: BarChart,
      link: '/trips/analytics',
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      title: 'Ratings & Reviews',
      description: 'Manage customer feedback and ratings',
      icon: Star,
      link: '/trips/ratings',
      color: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600'
    }
  ];

  const summarySection = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Trip Activity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Trip Activity</h3>
        <div className="space-y-4">
          {trips.slice(0, 5).map((trip) => (
            <div key={trip.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {trip.passenger.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {trip.pickup.name} → {trip.dropoff.name}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">
                  ${trip.fare.total}
                </p>
                <p className="text-xs text-gray-500">
                  {new Date(trip.requestedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trip Status Distribution */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Status Distribution</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">Completed</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{completedTrips}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-sm text-gray-600">Active</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{activeTrips}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-sm text-gray-600">Cancelled</span>
            </div>
            <span className="text-sm font-medium text-gray-900">{cancelledTrips}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-sm text-gray-600">Pending</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {trips.filter(trip => [TRIP_STATUS.REQUESTED, TRIP_STATUS.ASSIGNED].includes(trip.status)).length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <OverviewPageLayout
      title="Trip Management"
      subtitle="Comprehensive trip monitoring and management dashboard"
      kpiCards={kpiCards}
      quickActions={quickActions}
      summarySection={summarySection}
    />
  );
}
